// frontend/src/components/ConversationsList.tsx
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import Avatar from './Avatar';
import { Trash2, MessageSquareOff, MessageSquare, MoreVertical, X } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { socket } from '../socket';

dayjs.extend(relativeTime);

const API = API_URL.replace(/\/api$/, '');
const PLACEHOLDER = 'https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff';

export default function ConversationsList({
  token,
  onOpenConversation,
  currentUserId,
  onShowProfile,
  onlineUsers,
}: any) {
  const [conversations, setConversations] = useState<any[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [typingMap, setTypingMap] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState<string>('');
  const [showAll, setShowAll] = useState<boolean>(false);
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const refreshTimer = React.useRef<number | null>(null);
  const pollingTimer = React.useRef<number | null>(null);
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState<boolean>(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  // Removed long-press selection behavior

  // Close the options menu with Escape key
  useEffect(() => {
    if (!openMenuFor) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenuFor(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openMenuFor]);

  function updateConversations(list: any[]) {
    setConversations(list);
    const total = list.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0);
    setTotalUnread(total);
  }

  // Debug banner for WebView: show API URL, token presence, user id
  const [showDebugBanner, setShowDebugBanner] = useState<boolean>(() => {
    try {
      const dismissed = localStorage.getItem('dm-debug-banner-dismissed') === 'true';
      const inWebView = typeof window !== 'undefined' && (window as any).ReactNativeWebView;
      const debugEnabled = localStorage.getItem('DEBUG_WEBVIEW') === 'true';
      return inWebView && debugEnabled && !dismissed;
    } catch {
      return false;
    }
  });
  const debugInfo = React.useMemo(() => {
    try {
      const tokenSet = !!localStorage.getItem('token');
      const userRaw = localStorage.getItem('user') || '{}';
      const u = JSON.parse(userRaw);
      const uid = u?._id || u?.id || '';
      return { api: API || '', token: tokenSet ? 'present' : 'missing', userId: uid || 'unset' };
    } catch {
      return { api: API || '', token: 'unknown', userId: 'unknown' };
    }
  }, []);

  // Cache TTL in ms (2 minutes)
  const CACHE_KEY = 'auralink-conversations-cache';
  const CACHE_TTL = 2 * 60 * 1000;

  useEffect(() => {
    if (!token) return;
    // Show cached conversations instantly for perceived performance
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        const list = Array.isArray(cached?.data)
          ? cached.data
          : Array.isArray(cached)
            ? cached
            : [];
        const sorted = sortConversations(list);
        setConversations(sorted);
        setTotalUnread(sorted.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0));
        const age = cached?.ts ? Date.now() - cached.ts : CACHE_TTL + 1;
        if (age <= CACHE_TTL) {
          // Fresh cache; skip immediate network fetch
          setLoading(false);
        } else {
          setLoading(true);
          loadConversations();
        }
      } else {
        loadConversations();
      }
    } catch {
      loadConversations();
    }
    // Background silent refresh every 30s to keep unread counts fresh
    if (pollingTimer.current) {
      try {
        window.clearInterval(pollingTimer.current);
      } catch {}
      pollingTimer.current = null;
    }
    pollingTimer.current = window.setInterval(() => {
      loadConversations(true);
    }, 30000) as any;
    return () => {
      if (pollingTimer.current) {
        try {
          window.clearInterval(pollingTimer.current);
        } catch {}
        pollingTimer.current = null;
      }
    };
  }, [token]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Live updates: bump conversations on incoming messages
  useEffect(() => {
    function isActiveDM(convId: string): boolean {
      try {
        const inDM = JSON.parse(localStorage.getItem('auralink-in-dm') || 'false');
        const view = localStorage.getItem('auralink-current-view') || '';
        const raw = localStorage.getItem('auralink-active-conversation') || '';
        const active = raw ? JSON.parse(raw) : null;
        const activeId = active ? String(active._id || active.id) : '';
        return Boolean(inDM && view === 'chat' && activeId && String(convId) === activeId);
      } catch {
        return false;
      }
    }

    function onReceiveMessage(msg: any) {
      try {
        const convId = msg?.conversationId || msg?.conversation?._id;
        if (!convId) return;
        setConversations((prev) => {
          const idx = prev.findIndex((c: any) => String(c._id) === String(convId));
          if (idx === -1) return prev;
          const c = prev[idx];
          const isMine = String(msg?.sender?._id || msg?.senderId) === String(currentUserId);
          const active = isActiveDM(String(convId));
          const updated: any = {
            ...c,
            lastMessage:
              msg?.message || msg?.text
                ? {
                    text: msg.message || msg.text,
                    createdAt: msg.createdAt || Date.now(),
                    sender: msg.sender,
                  }
                : c.lastMessage,
            updatedAt: msg?.createdAt || Date.now(),
            // Do not bump unread for my own messages, nor when this DM is actively open
            unreadCount: isMine || active ? 0 : (c.unreadCount || 0) + 1,
          };
          const next = [...prev];
          next[idx] = updated;
          // Clear typing indicator on new message
          try {
            setTypingMap((m) => {
              const copy = { ...m };
              delete copy[String(convId)];
              return copy;
            });
          } catch {}
          return sortConversations(next);
        });
      } catch {}
    }

    function onTyping(payload: any) {
      try {
        const convId = payload?.conversationId;
        if (!convId) return;
        if (payload?.typing) {
          setTypingMap((m) => ({ ...m, [convId]: true }));
          // Bump conversation to top for visibility while typing
          setConversations((prev) => {
            const idx = prev.findIndex((c: any) => String(c._id) === String(convId));
            if (idx === -1) return prev;
            const next = [...prev];
            next[idx] = { ...next[idx], updatedAt: Date.now() };
            return sortConversations(next);
          });
          // Clear after 3s of inactivity
          window.setTimeout(() => {
            setTypingMap((m) => {
              const next = { ...m };
              delete next[convId];
              return next;
            });
          }, 3000);
        } else {
          setTypingMap((m) => {
            const next = { ...m };
            delete next[convId];
            return next;
          });
        }
      } catch {}
    }

    function onMessageEdited(payload: any) {
      try {
        const convId = payload?.conversationId || payload?.conversation?._id;
        if (!convId) return;
        setConversations((prev) => {
          const idx = prev.findIndex((c: any) => String(c._id) === String(convId));
          if (idx === -1) return prev;
          const c = prev[idx];
          const isLast = String(c.lastMessage?._id) === String(payload?._id);
          const updated = {
            ...c,
            lastMessage: isLast
              ? { ...c.lastMessage, text: payload?.text || payload?.message }
              : c.lastMessage,
          };
          const next = [...prev];
          next[idx] = updated;
          return next;
        });
      } catch {}
    }
    try {
      socket?.on('receive_message', onReceiveMessage);
      socket?.on('message_edited', onMessageEdited);
      // Also reflect temporary placeholders as activity
      socket?.on('temp_message', onReceiveMessage);
      socket?.on('typing', onTyping);
      socket?.on('conversation_read', (payload: any) => {
        const convId = payload?.conversationId;
        if (!convId) return;
        setConversations((prev) => {
          const idx = prev.findIndex((c: any) => String(c._id) === String(convId));
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], unreadCount: 0 };
          setTotalUnread(next.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0));
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: next })); } catch {}
          return next;
        });
      });
      // Also support immediate local read broadcasts from App without waiting on server
      const onLocalRead = (e: any) => {
        try {
          const convId = e?.detail?.conversationId;
          if (!convId) return;
          setConversations((prev) => {
            const idx = prev.findIndex((c: any) => String(c._id) === String(convId));
            if (idx === -1) return prev;
            const next = [...prev];
            next[idx] = { ...next[idx], unreadCount: 0 };
            setTotalUnread(next.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0));
            try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: next })); } catch {}
            return next;
          });
        } catch {}
      };
      window.addEventListener('auralink-conversation.read', onLocalRead as any);
    } catch {}
    return () => {
      try {
        socket?.off('receive_message', onReceiveMessage);
        socket?.off('message_edited', onMessageEdited);
        socket?.off('temp_message', onReceiveMessage);
        socket?.off('typing', onTyping);
        socket?.off('conversation_read');
        window.removeEventListener('auralink-conversation.read', onLocalRead as any);
      } catch {}
    };
  }, [currentUserId]);

  function loadConversations(silent: boolean = false) {
    if (!silent) setLoading(true);
    axios
      .get(API + '/api/conversations', {
        headers: { Authorization: 'Bearer ' + token },
      })
      .then((r) => {
        const convs = Array.isArray(r.data) ? r.data : r.data || [];
        const sorted = sortConversations(convs);
        updateConversations(sorted);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: sorted }));
        } catch {}
      })
      .catch((err) => {
        try {
          const status = err?.response?.status;
          const data = err?.response?.data;
          const url = (err?.config?.baseURL || '') + (err?.config?.url || '');
          console.error('ConversationsList error', { url, status, data });
        } catch {
          console.error('ConversationsList error', err);
        }
        // keep any cached list rather than clearing to empty
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }

  function sortConversations(list: any[]) {
    return [...list].sort((a, b) => {
      const aUnread = a.unreadCount || 0;
      const bUnread = b.unreadCount || 0;
      if (aUnread !== bUnread) return bUnread - aUnread;
      const aTime = new Date(a.updatedAt || a.lastMessage?.createdAt || 0).getTime();
      const bTime = new Date(b.updatedAt || b.lastMessage?.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }

  function toggleSelected(id: string) {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }
  function clearSelection() {
    setSelected({});
  }
  function selectAllVisible() {
    const next: Record<string, boolean> = {};
    for (const c of visible) next[String(c._id)] = true;
    setSelected(next);
  }

  // Long-press selection removed; use the explicit Select button instead

  async function bulkMarkRead() {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    if (!confirm(`Mark ${ids.length} conversation(s) as read?`)) return;
    try {
      await Promise.all(
        ids.map((id) =>
          axios.post(`${API}/api/conversations/${id}/read`, {}, {
            headers: { Authorization: 'Bearer ' + token },
          }),
        ),
      );
      // Optimistically zero unread
      setConversations((prev) => prev.map((c: any) => (selected[String(c._id)] ? { ...c, unreadCount: 0 } : c)));
      setTotalUnread((prev) => {
        const next = conversations.map((c: any) => (selected[String(c._id)] ? { ...c, unreadCount: 0 } : c));
        return next.reduce((sum: number, x: any) => sum + (x.unreadCount || 0), 0);
      });
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: conversations })); } catch {}
      clearSelection();
      setSelectMode(false);
    } catch (e) {
      console.error('Bulk mark read failed', e);
      alert('Failed to mark selected as read');
    }
  }

  async function bulkMarkUnread() {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    if (!confirm(`Mark ${ids.length} conversation(s) as unread?`)) return;
    try {
      await Promise.all(
        ids.map((id) =>
          axios.post(`${API}/api/conversations/${id}/unread`, {}, {
            headers: { Authorization: 'Bearer ' + token },
          }),
        ),
      );
      // Reload to refresh counts
      clearSelection();
      setSelectMode(false);
      loadConversations();
    } catch (e) {
      console.error('Bulk mark unread failed', e);
      alert('Failed to mark selected as unread');
    }
  }

  async function bulkDeleteConversations() {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} conversation(s) for you?`)) return;
    try {
      await Promise.all(
        ids.map((id) =>
          axios.delete(`${API}/api/conversations/${id}`, {
            headers: { Authorization: 'Bearer ' + token },
          }),
        ),
      );
      clearSelection();
      setSelectMode(false);
      loadConversations();
    } catch (e) {
      console.error('Bulk delete failed', e);
      alert('Failed to delete selected');
    }
  }

  async function bulkClearMessages() {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    if (!confirm(`Clear messages in ${ids.length} conversation(s)?`)) return;
    try {
      await Promise.all(
        ids.map((id) =>
          axios.delete(`${API}/api/conversations/${id}/messages`, {
            headers: { Authorization: 'Bearer ' + token },
          }),
        ),
      );
      clearSelection();
      setSelectMode(false);
      loadConversations();
    } catch (e) {
      console.error('Bulk clear messages failed', e);
      alert('Failed to clear messages for selected');
    }
  }

  function avatarUrl(u: any) {
    if (!u?.avatar) return PLACEHOLDER;
    if (u.avatar.startsWith('http')) return u.avatar;
    if (u.avatar.startsWith('/')) return API + u.avatar;
    return API + '/uploads/' + u.avatar;
  }

  async function handleDeleteConversation(convId: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm('Delete this conversation? (Only for you)')) return;

    try {
      await axios.delete(`${API}/api/conversations/${convId}`, {
        headers: { Authorization: 'Bearer ' + token },
      });

      // Reload conversations from server (will filter out if empty)
      loadConversations();
    } catch (err) {
      console.error('Delete conversation error:', err);
      alert('Failed to delete conversation');
    }
  }

  async function handleClearMessages(convId: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm('Clear all messages in this chat? (Only for you)')) return;

    try {
      await axios.delete(`${API}/api/conversations/${convId}/messages`, {
        headers: { Authorization: 'Bearer ' + token },
      });

      // Reload conversations from server (will filter out if empty)
      loadConversations();
    } catch (err) {
      console.error('Clear messages error:', err);
      alert('Failed to clear messages');
    }
  }

  const filtered = React.useMemo(() => {
    const q = debouncedQuery;
    if (!q) return conversations;
    return conversations.filter((c: any) => {
      const partner = (c.participants || []).find(
        (p: any) => String(p._id) !== String(currentUserId),
      );
      const u = partner || {};
      return (
        String(u.username || '')
          .toLowerCase()
          .includes(q) ||
        String(u.status || '')
          .toLowerCase()
          .includes(q)
      );
    });
  }, [conversations, debouncedQuery, currentUserId]);

  const visible = React.useMemo(() => {
    if (showAll) return filtered;
    return filtered.slice(0, 50);
  }, [filtered, showAll]);

  // Long-press removed; use explicit three-dots menu per conversation

  return (
    <div className="flex flex-col gap-2 sm:gap-3">
      {showDebugBanner && (
        <div
          className="rounded-lg px-3 py-2 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 border"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <div>
                <span className="font-semibold">Debug:</span> API_URL = {debugInfo.api || 'unset'}
              </div>
              <div>
                Token = {debugInfo.token}; UserId = {debugInfo.userId}
              </div>
            </div>
            <button
              className="text-[11px] px-2 py-1 rounded-md bg-white/60 dark:bg-slate-700/60 hover:opacity-80"
              onClick={() => {
                try {
                  localStorage.setItem('dm-debug-banner-dismissed', 'true');
                } catch {}
                setShowDebugBanner(false);
              }}
            >
              Hide
            </button>
          </div>
        </div>
      )}
      {/* Long-press hint removed; explicit menu button is visible */}
      {/* Total Unread Badge - only show if there are actual conversations with unread messages */}
      {!loading && conversations.length > 0 && totalUnread > 0 && (
        <div className="sticky top-0 z-10 bg-gradient-to-r from-emerald-500 to-indigo-600 text-white px-4 py-2 rounded-xl text-center font-semibold shadow-lg ring-1 ring-white/20">
          {totalUnread} unread message{totalUnread !== 1 ? 's' : ''}
        </div>
      )}

      {/* Search + Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <input
          className="input w-full sm:w-64"
          placeholder="Search conversations"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap">
          <button
            className="text-xs px-3 py-1.5 rounded-md border"
            style={{ borderColor: 'var(--border)' }}
            onClick={() => setSelectMode((v) => !v)}
            title={selectMode ? 'Exit selection' : 'Select conversations'}
          >
            {selectMode ? 'Done' : 'Select'}
          </button>
          {selectMode && (
            <div className="flex items-center gap-2">
              <button className="text-[11px] px-2 py-1.5 rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={selectAllVisible}>Select all</button>
              <button className="text-[11px] px-2 py-1.5 rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={clearSelection}>Clear</button>
              <button className="text-[11px] px-2 py-1.5 rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={bulkMarkRead}>Mark read</button>
              <button className="text-[11px] px-2 py-1.5 rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={bulkMarkUnread}>Unread</button>
              <button className="text-[11px] px-2 py-1.5 rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={bulkClearMessages}>Clear msgs</button>
              <button className="text-[11px] px-2 py-1.5 rounded-md border text-red-600" style={{ borderColor: 'var(--border)' }} onClick={bulkDeleteConversations}>Delete</button>
            </div>
          )}
          <button
            className="text-xs px-3 py-1.5 rounded-md border"
            style={{ borderColor: 'var(--border)' }}
            onClick={() => loadConversations()}
            title="Refresh"
          >
            Refresh
          </button>
          {filtered.length > 50 && (
            <button
              className="text-xs px-3 py-1.5 rounded-md border"
              style={{ borderColor: 'var(--border)' }}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? 'Show first 50' : `Show all (${filtered.length})`}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="p-3 rounded-lg border animate-pulse"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-md" style={{ background: 'var(--muted)' }} />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/3 rounded" style={{ background: 'var(--muted)' }} />
                  <div className="h-3 w-1/4 rounded" style={{ background: 'var(--muted)' }} />
                </div>
                <div className="h-8 w-24 rounded" style={{ background: 'var(--muted)' }} />
              </div>
            </div>
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 dark:bg-slate-800/60 border border-white/20 text-cyan-400 shadow-sm mb-3">
            <MessageSquare className="w-9 h-9" />
          </div>
          <div className="text-heading font-semibold">No conversations yet</div>
          <div className="text-xs text-theme-secondary mt-2">Start chatting with users from the All Users page</div>
        </div>
      ) : (
        visible.map((c: any) => {
          let partner = (c.participants || []).find(
            (p: any) => p && String(p._id) !== String(currentUserId),
          );

          if (!partner) {
            // Fallback for deleted users: show placeholder participant
            partner = { _id: 'deleted', username: 'Deleted User', avatar: '' } as any;
          }

          const unreadCount = c.unreadCount || 0;

          return (
            <div
              key={c._id}
              className={`
                flex flex-col sm:flex-row sm:items-center sm:justify-between 
                p-3 sm:p-4 rounded-xl 
                bg-white dark:bg-slate-800
                border transition-all overflow-visible
                ${
                  unreadCount > 0
                    ? 'border-emerald-400 dark:border-emerald-500 shadow-md shadow-emerald-100 dark:shadow-emerald-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:shadow-lg hover:ring-1 hover:ring-cyan-400/30'
                }
                gap-3 relative
                ${selectMode ? 'pl-10 sm:pl-14' : ''}
              `}
              // Long-press handlers removed
              
            >
              {selectMode && (
                <div className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-10">
                  <input
                    type="checkbox"
                    className="scale-110"
                    checked={!!selected[String(c._id)]}
                    onChange={() => toggleSelected(String(c._id))}
                    aria-label="Select conversation"
                  />
                </div>
              )}
              {/* Unread Badge */}
              {unreadCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-emerald-500 to-indigo-600 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-lg">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </div>
              )}

              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="relative flex-shrink-0">
                  <Avatar
                    src={avatarUrl(partner)}
                    onClick={() => partner?._id !== 'deleted' && onShowProfile(partner)}
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-md object-cover cursor-pointer hover:scale-105 transition-transform"
                    alt={partner?.username || 'User'}
                    loading="lazy"
                  />
                  {/* Online status indicator */}
                  {onlineUsers?.has(partner._id) && (
                    <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white dark:border-slate-800 rounded-full shadow-sm"></div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div
                    className={`font-bold truncate ${unreadCount > 0 ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-900 dark:text-slate-100'} cursor-pointer`}
                    onClick={() => {
                      if (selectMode) {
                        toggleSelected(String(c._id));
                      } else {
                        // Optimistically zero unread and open chat when clicking name
                        setConversations((prev) => {
                          const idx = prev.findIndex((x: any) => String(x._id) === String(c._id));
                          if (idx === -1) return prev;
                          const next = [...prev];
                          next[idx] = { ...next[idx], unreadCount: 0 };
                          setTotalUnread(
                            next.reduce((sum: number, x: any) => sum + (x.unreadCount || 0), 0),
                          );
                          try {
                            localStorage.setItem(
                              CACHE_KEY,
                              JSON.stringify({ ts: Date.now(), data: next }),
                            );
                          } catch {}
                          return next;
                        });
                        onOpenConversation(c);
                      }
                    }}
                    title="Open chat"
                  >
                    {partner?.username || 'User'}
                  </div>
                  {/* Last message preview + relative time */}
                  <div className="text-xs mt-0.5 text-slate-500 dark:text-slate-400 truncate">
                    {typingMap[c._id] ? (
                      <span className="text-cyan-600 dark:text-cyan-400 animate-pulse">
                        {(partner?.username || 'User')} is typing…
                      </span>
                    ) : (
                      <>
                        {c.lastMessage?.text ? c.lastMessage.text : c.lastMessage ? '(attachment)' : ''}
                        {c.lastMessage?.createdAt && (
                          <span className="ml-2 text-[10px] text-slate-400">
                            • {dayjs(c.lastMessage.createdAt).fromNow()}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto relative">
                <button
                  className="text-xs px-3 py-1.5 rounded-md border"
                  style={{ borderColor: 'var(--border)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (partner?._id !== 'deleted') onShowProfile(partner);
                  }}
                  title="View Profile"
                >
                  View Profile
                </button>
                {/* Three-dots menu button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuFor((id) => (id === c._id ? null : c._id));
                  }}
                  className="p-2 rounded-md hover:opacity-80"
                  title="More"
                  aria-haspopup="menu"
                  aria-expanded={openMenuFor === c._id}
                >
                  <MoreVertical className="w-5 h-5 text-theme-secondary" />
                </button>
                {openMenuFor === c._id && (
                  <>
                    {/* Overlay: click to close menu on desktop/mobile */}
                    <div
                      className="fixed inset-0 z-40 bg-black/40"
                      aria-label="Close options"
                      onClick={() => setOpenMenuFor(null)}
                    />
                    {/* Responsive options menu: bottom sheet on mobile, fixed popover on desktop */}
                    <div
                      className="fixed inset-x-4 bottom-4 z-50 rounded-2xl shadow-2xl border p-2 sm:fixed sm:top-20 sm:right-6 sm:left-auto sm:bottom-auto sm:w-80 sm:rounded-xl sm:shadow-xl"
                      style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                      onClick={(e) => e.stopPropagation()}
                      role="menu"
                      aria-label="Conversation options"
                    >
                      <div className="sm:hidden flex justify-center py-1">
                        <div className="w-10 h-1.5 rounded-full" style={{ background: 'var(--muted)' }} />
                      </div>
                      <div className="flex items-center justify-between px-2 pb-2 sm:hidden">
                        <div className="text-xs text-theme-secondary">Conversation Options</div>
                        <button
                          onClick={() => setOpenMenuFor(null)}
                          className="themed-card rounded-full p-1"
                          aria-label="Close options"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <button
                        onClick={(e) => {
                          handleClearMessages(c._id, e);
                          setOpenMenuFor(null);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm hover:bg-black/5 dark:hover:bg-white/10 rounded-lg"
                        role="menuitem"
                      >
                        <MessageSquareOff className="w-4 h-4" /> Clear Messages
                      </button>
                      <button
                        onClick={(e) => {
                          handleDeleteConversation(c._id, e);
                          setOpenMenuFor(null);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg"
                        role="menuitem"
                      >
                        <Trash2 className="w-4 h-4" /> Delete Conversation
                      </button>

                      <div className="mt-2 flex sm:hidden">
                        <button
                          onClick={() => setOpenMenuFor(null)}
                          className="w-full px-4 py-2 themed-card rounded-lg"
                          aria-label="Close"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
